import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Star, RefreshCw, ThumbsUp, ThumbsDown } from "lucide-react";
import { Button } from "@/components/shadcn/button";
import { feedbackApi } from "@/api/feedback";
import { playerFeedbackApi, playerFeedbackQueryOptions } from "@/api/playerFeedback";
import { MarkdownRenderer } from "@/components/chat/MarkdownRenderer";
import { cn } from "@/lib/utils";
import { logger } from "@/lib/logger";

interface PlayerFeedbackPanelProps {
    playerId: number;
    tournamentId: number;
    teamId: number;
}

export function PlayerFeedbackPanel({ playerId, tournamentId, teamId }: PlayerFeedbackPanelProps) {
    const [userFeedback, setUserFeedback] = useState<number | null>(null);
    const queryClient = useQueryClient();

    const { data: feedbackData, isLoading } = useQuery(playerFeedbackQueryOptions(playerId, tournamentId, teamId));

    const regenerateMutation = useMutation({
        mutationFn: () => playerFeedbackApi.regenerateFeedback(playerId, tournamentId, teamId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["player-feedback", playerId, tournamentId, teamId] });
            setUserFeedback(null);
        },
        onError: (error) => {
            logger.error("Failed to regenerate feedback:", error);
        },
    });

    const feedbackMutation = useMutation({
        mutationFn: ({ rating }: { rating: number }) =>
            feedbackApi.submitFeedback({
                entityType: "player_feedback",
                entityId: feedbackData!.id.toString(),
                feedbackValue: rating === 1,
            }),
        onSuccess: (_, { rating }) => {
            setUserFeedback(rating);
        },
        onError: (error) => {
            logger.error("Failed to submit feedback:", error);
        },
    });

    useEffect(() => {
        setUserFeedback(null);
    }, [feedbackData?.id]);

    const isRegenerating = regenerateMutation.isPending;
    const isSubmittingFeedback = feedbackMutation.isPending;

    return (
        <>
            <div className="relative px-4 pt-3 pb-1">
                <h3 className="text-sm font-headline font-bold section-title">Lead Scout Commentary</h3>
                {feedbackData && !isLoading && (
                    <div className="absolute right-0 top-1/2 -translate-y-1/2">
                        <Button
                            onClick={() => regenerateMutation.mutate()}
                            disabled={isRegenerating}
                            variant="outlined"
                            size="sm"
                            className="gap-1.5"
                        >
                            {isRegenerating ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <RefreshCw className="w-4 h-4" />
                            )}
                            Regenerate
                        </Button>
                    </div>
                )}
            </div>

            <div className="flex-1 min-h-[220px] rounded-xl overflow-hidden bg-surface-container-highest">
                {isLoading ? (
                    <div className="flex items-center justify-center h-full py-16">
                        <div className="flex items-center gap-3 text-on-surface-variant">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span className="text-sm">Generating analysis...</span>
                        </div>
                    </div>
                ) : !feedbackData ? (
                    <div className="flex flex-col items-center justify-center h-full py-16 gap-3 text-on-surface-variant">
                        <Star className="w-8 h-8 opacity-30" />
                        <p className="text-sm">No commentary available</p>
                    </div>
                ) : (
                    <div className="flex flex-col h-full">
                        <div className="flex-1 px-6 py-5 overflow-y-auto">
                            <div className="prose prose-sm max-w-none text-on-surface">
                                <MarkdownRenderer content={feedbackData.feedback} />
                            </div>
                        </div>
                        <div className="flex items-center justify-between px-6 py-3 border-t border-outline-variant/15 bg-surface-container-low shrink-0">
                            <p className="text-xs text-on-surface-variant font-medium">Was this analysis helpful?</p>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => feedbackMutation.mutate({ rating: 1 })}
                                    disabled={isSubmittingFeedback || userFeedback !== null}
                                    className={cn(
                                        "p-1.5 rounded-md transition-all flex items-center justify-center cursor-pointer",
                                        userFeedback === 1
                                            ? "bg-green-100 text-green-700"
                                            : "hover:bg-surface-container text-on-surface-variant hover:text-green-700",
                                        "disabled:opacity-50 disabled:cursor-not-allowed"
                                    )}
                                    title="Helpful"
                                >
                                    {isSubmittingFeedback && userFeedback === null ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <ThumbsUp className="w-4 h-4" />
                                    )}
                                </button>
                                <button
                                    onClick={() => feedbackMutation.mutate({ rating: 0 })}
                                    disabled={isSubmittingFeedback || userFeedback !== null}
                                    className={cn(
                                        "p-1.5 rounded-md transition-all flex items-center justify-center cursor-pointer",
                                        userFeedback === 0
                                            ? "bg-red-100 text-red-700"
                                            : "hover:bg-surface-container text-on-surface-variant hover:text-red-700",
                                        "disabled:opacity-50 disabled:cursor-not-allowed"
                                    )}
                                    title="Not helpful"
                                >
                                    {isSubmittingFeedback && userFeedback === null ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <ThumbsDown className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
